// Iter XX.216 — Sticky bottom banner surfacing the Android download CTA.
// Iter XX.217 — Now renders on /chat too, in a compact top-slim
// variant so it never covers the fixed composer.
//
// Mounted globally in App.js; guards its own visibility via
// `shouldShowAndroidDownloadCta()`. Dismissable, session-scoped.
//
// Layout:
//   · Landing / marketing routes → full banner fixed to viewport bottom
//     (prominent, respects safe-area-inset-bottom).
//   · /chat → compact one-liner fixed to the TOP of the viewport,
//     directly below the chat header. Never touches the composer;
//     respects safe-area-inset-top; unaffected by mobile keyboard.
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import {
  shouldShowAndroidDownloadCta,
  resolveStoreDestination,
  markBannerDismissed,
  trackDownloadCtaView,
  trackDownloadCtaClick,
  isAndroidWeb,
  isIOSSafari,
} from "../lib/androidDownloadCta";

export default function AndroidDownloadBanner() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  // Iter XX.217 — `/chat` and its sub-routes get the compact top
  // variant; every other Meta-landing / marketing surface keeps
  // the prominent bottom banner.
  const isChatSurface = (location.pathname || "").startsWith("/chat");
  const variant = isChatSurface ? "compact" : "full";
  const placement = isChatSurface
    ? "sticky_chat_banner"
    : "sticky_landing_banner";

  useEffect(() => {
    // Recompute visibility only after mount so SSR / hydration
    // mismatch on `navigator.userAgent` is impossible.
    if (shouldShowAndroidDownloadCta()) {
      setVisible(true);
      trackDownloadCtaView({ placement });
    }
  }, [placement]);

  if (!visible) return null;

  const href = resolveStoreDestination();
  if (!href) return null;

  const onClick = () => {
    trackDownloadCtaClick({ placement });
  };

  const onDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    markBannerDismissed();
    setVisible(false);
  };

  if (variant === "compact") {
    // Compact one-liner pinned to the top, directly under the chat
    // header. Never covers the composer (bottom), respects notch/
    // status-bar via safe-area-inset-top. Slim height keeps message
    // reading area unobstructed. Mobile keyboard opens the virtual
    // viewport from the BOTTOM — top-pinned banner is inherently
    // keyboard-safe.
    return (
      <div
        role="region"
        aria-label="Download Gully"
        data-testid="android-download-sticky-banner"
        data-testid-variant="compact"
        className="fixed inset-x-0 top-0 z-[60] pointer-events-none"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="mx-auto max-w-3xl px-3 pt-2 pointer-events-auto">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            data-testid="android-download-cta-link"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-700/60 bg-neutral-950/90 backdrop-blur-sm shadow hover:border-emerald-500/80 hover:bg-neutral-900/95 transition-colors"
          >
            <span aria-hidden className="text-base leading-none">🏏</span>
            <span className="flex-1 min-w-0 text-[12px] text-emerald-100 font-medium truncate">
              Get the Gully app
            </span>
            <span
              data-testid="android-download-cta-button"
              className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-neutral-950 text-[11px] font-semibold tracking-wide transition-colors"
            >
              Download
            </span>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              data-testid="android-download-cta-dismiss"
              className="shrink-0 -mr-0.5 p-0.5 rounded-full text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800/60 transition-colors"
            >
              <X size={13} strokeWidth={2} />
            </button>
          </a>
        </div>
      </div>
    );
  }

  // Full variant — landing / marketing surfaces.
  // XX.248 — iOS visitors see App Store copy; Android web on-device
  // sees "now on Android"; Android web from desktop links fall back
  // to the neutral "on Google Play" line. Never says "Android" on iOS.
  const iosVisitor = isIOSSafari();
  const androidTagline = iosVisitor
    ? "Gully is on the App Store"
    : (isAndroidWeb() ? "Gully is now on Android" : "Gully is on Google Play");

  return (
    <div
      role="region"
      aria-label="Download Gully"
      data-testid="android-download-sticky-banner"
      data-testid-variant="full"
      data-store-platform={iosVisitor ? "ios" : (isAndroidWeb() ? "android" : "web")}
      className="fixed inset-x-0 z-[70] pointer-events-none"
      style={{
        // Iter XX.286 — Sit ABOVE the persistent <MobileBottomNav>
        // (which publishes `--gully-bottom-nav-h` = measured nav
        // height including the iOS safe-area inset). On surfaces
        // that don't mount the nav (landing / marketing / desktop),
        // fall back to the safe-area inset so the banner still
        // clears the iOS home indicator.
        bottom: "var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px))",
      }}
    >
      <div className="mx-auto max-w-3xl px-3 pb-3 pointer-events-auto">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          data-testid="android-download-cta-link"
          className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-emerald-700/60 bg-neutral-950/95 backdrop-blur-sm shadow-lg hover:border-emerald-500/80 hover:bg-neutral-900/95 transition-colors"
        >
          <span aria-hidden className="text-2xl leading-none">🏏</span>
          <span className="flex-1 min-w-0">
            <span className="block text-[13px] sm:text-sm font-semibold text-emerald-100 truncate">
              {androidTagline}
            </span>
            <span className="block text-xs text-neutral-400 truncate">
              Your cricket. Remembered.
            </span>
          </span>
          <span
            data-testid="android-download-cta-button"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-neutral-950 text-xs font-semibold tracking-wide transition-colors"
          >
            Download Gully
          </span>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            data-testid="android-download-cta-dismiss"
            className="shrink-0 -mr-1 p-1 rounded-full text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800/60 transition-colors"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </a>
      </div>
    </div>
  );
}
